<template>
    <div class="fm-preview">
        <div class="fm-preview-header">
            <span class="fm-preview-header-name">{{ $t('common.fileManager.preview') }}</span>
            <div class="fm-preview-header-actions">
                <button class="fm-preview-close" @click="$emit('close')">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                </button>
            </div>
        </div>
        <div class="fm-preview-content" v-if="file">
            <div class="fm-preview-visual" :class="visualClass">
                <template v-if="isImage && (file.thumbnailUrl || hasPublicUrl)">
                    <img :src="file.thumbnailUrl || file.publicUrl" :alt="file.name" @click="$emit('lightbox', file)"/>
                    <div class="fm-preview-img-overlay" @click="$emit('lightbox', file)">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/><line x1="11" y1="8" x2="11" y2="14"/><line x1="8" y1="11" x2="14" y2="11"/></svg>
                    </div>
                </template>
                <video v-else-if="isVideo && hasPublicUrl"
                       :src="file.publicUrl" controls/>
                <template v-else-if="isAudio && hasPublicUrl">
                    <div class="fm-preview-audio-visual">
                        <template v-if="file.thumbnailUrl && file.meta?.has_cover">
                            <img :src="file.thumbnailUrl" :alt="file.name" class="fm-preview-audio-cover"/>
                        </template>
                        <template v-else>
                            <div class="fm-preview-audio-bars">
                                <span v-for="i in 24" :key="i" :style="{animationDelay: (i * 0.07) + 's', height: barHeight(i) + '%'}"></span>
                            </div>
                            <span :class="'fm-icon-color--audio'" v-html="iconSvg" class="fm-preview-audio-icon"></span>
                        </template>
                    </div>
                    <audio :src="file.publicUrl" controls class="fm-preview-audio-player"/>
                </template>
                <template v-else-if="isPdf">
                    <template v-if="hasPublicUrl">
                        <iframe :src="file.publicUrl + '#toolbar=0&navpanes=0'" class="fm-preview-pdf-iframe"></iframe>
                        <div class="fm-preview-pdf-overlay" @click="openPdfViewer">
                            <button class="fm-preview-pdf-btn">
                                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" width="14" height="14"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                                {{ $t('common.fileManager.pdfPreview') }}
                            </button>
                        </div>
                    </template>
                    <div v-else class="fm-preview-visual-placeholder">
                        <span :class="'fm-icon-color--pdf'" v-html="iconSvg"></span>
                        <span>{{ $t('common.fileManager.pdfFile') }}</span>
                    </div>
                </template>
                <div v-else class="fm-preview-visual-placeholder" :style="placeholderStyle">
                    <span :class="'fm-icon-color--' + iconType" v-html="iconSvg"></span>
                </div>
            </div>

            <div class="fm-preview-name-row">
                <div class="fm-preview-name-scroll" ref="nameWrap">
                    <span class="fm-preview-filename" ref="nameText">{{ nameWithoutExt }}</span>
                </div>
                <span v-if="file.extension" class="fm-preview-ext-pill" :style="extPillStyle">{{ (file.extension || '').toUpperCase() }}</span>
            </div>

            <div class="fm-preview-meta-card">
                <div class="fm-preview-meta-row">
                    <span class="fm-preview-meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/><line x1="16" y1="5" x2="22" y2="5"/><line x1="19" y1="2" x2="19" y2="8"/></svg></span>
                    <span class="fm-preview-meta-label">{{ $t('common.fileManager.metaSize') }}</span>
                    <span class="fm-preview-meta-value">{{ fm.formatFileSize(file.size) }}</span>
                </div>
                <div class="fm-preview-meta-row">
                    <span class="fm-preview-meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg></span>
                    <span class="fm-preview-meta-label">{{ $t('common.fileManager.metaType') }}</span>
                    <span class="fm-preview-meta-value">{{ $t('common.fileManager.fileType_' + iconType) }}</span>
                </div>
                <div class="fm-preview-meta-row">
                    <span class="fm-preview-meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2" ry="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg></span>
                    <span class="fm-preview-meta-label">{{ $t('common.fileManager.metaCreated') }}</span>
                    <span class="fm-preview-meta-value">{{ fm.formatDate(file.createdAt) }}</span>
                </div>
                <div class="fm-preview-meta-row">
                    <span class="fm-preview-meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg></span>
                    <span class="fm-preview-meta-label">{{ $t('common.fileManager.metaModified') }}</span>
                    <span class="fm-preview-meta-value">{{ fm.formatDate(file.updatedAt) }}</span>
                </div>
                <div v-if="file.module" class="fm-preview-meta-row">
                    <span class="fm-preview-meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><line x1="22" y1="12" x2="2" y2="12"/></svg></span>
                    <span class="fm-preview-meta-label">{{ $t('common.fileManager.metaModule') }}</span>
                    <span :class="['fm-preview-meta-value', {'fm-preview-entity-link': !!file.moduleEntityId}]"
                          @click="navigateToEntity">{{ moduleLabel }}</span>
                </div>
                <div v-if="file.mimeType" class="fm-preview-meta-row">
                    <span class="fm-preview-meta-icon"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="16 18 22 12 16 6"/><polyline points="8 6 2 12 8 18"/></svg></span>
                    <span class="fm-preview-meta-label">MIME</span>
                    <span class="fm-preview-meta-value fm-preview-meta-value--small">{{ file.mimeType }}</span>
                </div>
                <template v-for="row in metaRows" :key="row.label">
                    <div class="fm-preview-meta-row">
                        <span class="fm-preview-meta-icon" v-html="row.icon"></span>
                        <span class="fm-preview-meta-label">{{ row.label }}</span>
                        <span class="fm-preview-meta-value">{{ row.value }}</span>
                    </div>
                </template>
            </div>

            <div class="fm-preview-actions">
                <button v-if="hasPublicUrl" class="fm-preview-action-btn fm-preview-action-btn--primary" @click="downloadFile">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {{ $t('common.fileManager.download') }}
                </button>
                <button v-if="file.module && file.moduleEntityId" class="fm-preview-action-btn fm-preview-action-btn--secondary" @click="navigateToEntity">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"/><polyline points="15 3 21 3 21 9"/><line x1="10" y1="14" x2="21" y2="3"/></svg>
                    {{ $t('common.fileManager.goToContent') }}
                </button>
            </div>
        </div>

        <teleport to="body">
            <transition name="fm-pdf-viewer">
                <div v-if="showPdfViewer" class="fm-pdf-viewer" @keydown.esc="showPdfViewer = false" tabindex="0" ref="pdfViewer">
                    <div class="fm-pdf-viewer-header">
                        <span class="fm-pdf-viewer-name">{{ file.name }}</span>
                        <button class="fm-pdf-viewer-close" @click="showPdfViewer = false">
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
                        </button>
                    </div>
                    <iframe v-if="showPdfViewer" :src="file.publicUrl" class="fm-pdf-viewer-iframe"></iframe>
                </div>
            </transition>
        </teleport>
    </div>
</template>

<script>
import fileManagerService from './fileManagerService'

const TYPE_COLORS = {
    pdf: '#EF4444', document: '#3B82F6', spreadsheet: '#10B981',
    image: '#8B5CF6', archive: '#F59E0B', video: '#EC4899',
    audio: '#F97316', generic: '#6B7280',
}

export default {
    name: 'FileManagerPreview',
    props: {
        file: { type: Object, default: null },
    },
    emits: ['close', 'download', 'lightbox'],
    data() {
        return {
            showPdfViewer: false,
        }
    },
    computed: {
        fm() { return fileManagerService },
        iconType() {
            return fileManagerService.getFileIcon(this.file?.extension)
        },
        iconSvg() {
            return fileManagerService.getFileIconSvg(this.file?.extension)
        },
        isImage() {
            return this.iconType === 'image'
        },
        isPdf() {
            return this.iconType === 'pdf'
        },
        isVideo() {
            return this.iconType === 'video'
        },
        isAudio() {
            return this.iconType === 'audio'
        },
        hasPublicUrl() {
            return this.file?.publicUrl && this.file.publicUrl !== '#'
        },
        moduleLabel() {
            if (!this.file?.module) return ''
            return fileManagerService.resolveEntityLabel(this.file.module, this.file.moduleEntityId, this.$t)
        },
        truncatedName() {
            const name = this.file?.name || ''
            return name.length > 28 ? name.slice(0, 26) + '…' : name
        },
        nameWithoutExt() {
            const name = this.file?.name || ''
            const ext = this.file?.extension
            if (ext && name.toLowerCase().endsWith('.' + ext.toLowerCase())) {
                return name.slice(0, -(ext.length + 1))
            }
            return name
        },
        typeColor() {
            return TYPE_COLORS[this.iconType] || TYPE_COLORS.generic
        },
        extPillStyle() {
            const c = this.typeColor
            return { background: c + '14', color: c }
        },
        placeholderStyle() {
            return { background: this.typeColor + '0A' }
        },
        visualClass() {
            return {
                'fm-preview-visual--image': this.isImage && (this.file?.thumbnailUrl || this.hasPublicUrl),
                'fm-preview-visual--video': this.isVideo,
                'fm-preview-visual--audio': this.isAudio,
                'fm-preview-visual--pdf': this.isPdf,
            }
        },
        metaRows() {
            const m = this.file?.meta
            if (!m) return []
            const rows = []
            const ico = {
                res: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="3" width="20" height="14" rx="2" ry="2"/><line x1="8" y1="21" x2="16" y2="21"/><line x1="12" y1="17" x2="12" y2="21"/></svg>',
                cam: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>',
                lens: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>',
                map: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/></svg>',
                play: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><polygon points="10 8 16 12 10 16 10 8"/></svg>',
                film: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="2" y="2" width="20" height="20" rx="2.18" ry="2.18"/><line x1="7" y1="2" x2="7" y2="22"/><line x1="17" y1="2" x2="17" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/><line x1="2" y1="7" x2="7" y2="7"/><line x1="2" y1="17" x2="7" y2="17"/><line x1="17" y1="7" x2="22" y2="7"/><line x1="17" y1="17" x2="22" y2="17"/></svg>',
                page: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/></svg>',
                music: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 18V5l12-2v13"/><circle cx="6" cy="18" r="3"/><circle cx="18" cy="16" r="3"/></svg>',
                user: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
                disc: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><circle cx="12" cy="12" r="3"/></svg>',
            }
            const res = m.resolution
            if (res?.width && res?.height) {
                rows.push({icon: ico.res, label: this.$t('common.fileManager.metaResolution'), value: res.width + ' × ' + res.height})
            }
            if (m.duration != null) {
                const dur = Math.round(m.duration)
                const min = Math.floor(dur / 60)
                const sec = dur % 60
                rows.push({icon: ico.play, label: this.$t('common.fileManager.metaDuration'), value: min + ':' + String(sec).padStart(2, '0')})
            }
            if (m.exif) {
                if (m.exif.camera) {
                    rows.push({icon: ico.cam, label: this.$t('common.fileManager.metaCamera'), value: m.exif.camera + (m.exif.make && !m.exif.camera.includes(m.exif.make) ? ' (' + m.exif.make + ')' : '')})
                }
                const parts = []
                if (m.exif.aperture) parts.push('ƒ/' + m.exif.aperture)
                if (m.exif.exposure_time) parts.push(m.exif.exposure_time + 's')
                if (m.exif.iso) parts.push('ISO ' + m.exif.iso)
                if (m.exif.focal_length) parts.push(m.exif.focal_length + 'mm')
                if (parts.length) {
                    rows.push({icon: ico.lens, label: this.$t('common.fileManager.metaExposure'), value: parts.join('  ·  ')})
                }
                if (m.exif.taken_at) {
                    const d = m.exif.taken_at.replace(/^(\d{4}):(\d{2}):(\d{2})/, '$1-$2-$3')
                    rows.push({icon: ico.cam, label: this.$t('common.fileManager.metaTakenAt'), value: fileManagerService.formatDate(d)})
                }
                if (m.exif.gps?.lat != null && m.exif.gps?.lng != null) {
                    rows.push({icon: ico.map, label: this.$t('common.fileManager.metaGps'), value: m.exif.gps.lat.toFixed(5) + ', ' + m.exif.gps.lng.toFixed(5)})
                }
            }
            if (m.fps) {
                rows.push({icon: ico.film, label: 'FPS', value: m.fps})
            }
            if (m.bitrate && (this.isVideo || this.isAudio)) {
                const kbps = Math.round(m.bitrate / 1000)
                rows.push({icon: ico.disc, label: 'Bitrate', value: kbps + ' kbps'})
            }
            if (m.audio_codec) {
                rows.push({icon: ico.music, label: this.$t('common.fileManager.metaCodec'), value: m.audio_codec})
            }
            if (m.page_count) {
                rows.push({icon: ico.page, label: this.$t('common.fileManager.metaPages'), value: m.page_count})
            }
            if (m.tags) {
                if (m.tags.artist) rows.push({icon: ico.user, label: this.$t('common.fileManager.metaArtist'), value: m.tags.artist})
                if (m.tags.album) rows.push({icon: ico.disc, label: this.$t('common.fileManager.metaAlbum'), value: m.tags.album})
                if (m.tags.genre) rows.push({icon: ico.music, label: this.$t('common.fileManager.metaGenre'), value: m.tags.genre})
            }
            return rows
        },
    },
    watch: {
        showPdfViewer(v) {
            if (v) this.$nextTick(() => this.$refs.pdfViewer?.focus())
        },
        file() {
            this.showPdfViewer = false
            this._stopNameScroll()
            this.$nextTick(() => this._checkNameScroll())
        },
    },
    mounted() {
        this.$nextTick(() => this._checkNameScroll())
    },
    beforeUnmount() {
        this._stopNameScroll()
    },
    methods: {
        navigateToEntity() {
            if (!this.file?.module || !this.file?.moduleEntityId) return
            fileManagerService.navigateEntity(this.file.module, this.file.moduleEntityId, this.$router)
        },
        downloadFile() {
            this.$emit('download', this.file)
        },
        openPdfViewer() {
            this.showPdfViewer = true
        },
        closePdfViewer() {
            this.showPdfViewer = false
        },
        barHeight(i) {
            return 20 + Math.abs(Math.sin(i * 0.8)) * 60
        },
        _checkNameScroll() {
            const wrap = this.$refs.nameWrap
            const text = this.$refs.nameText
            if (!wrap || !text) return
            text.style.transform = ''
            text.style.transition = ''
            if (text.scrollWidth > wrap.clientWidth) {
                const dist = text.scrollWidth - wrap.clientWidth
                text.style.setProperty('--scroll-dist', `-${dist}px`)
                this._nameScrollPhase = 0
                this._nameScrollTimer = setInterval(() => this._tickNameScroll(text, dist), 3000)
            }
        },
        _tickNameScroll(text, dist) {
            if (!text) { this._stopNameScroll(); return }
            this._nameScrollPhase = this._nameScrollPhase === 0 ? 1 : 0
            const duration = Math.max(1.5, dist / 50)
            if (this._nameScrollPhase === 1) {
                text.style.transition = `transform ${duration}s linear`
                text.style.transform = `translateX(-${dist}px)`
            } else {
                text.style.transition = `transform ${duration}s linear`
                text.style.transform = 'translateX(0)'
            }
        },
        _stopNameScroll() {
            clearInterval(this._nameScrollTimer)
            this._nameScrollTimer = null
        },
    },
}
</script>
